import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { apiService, WatermarkSettings, WatermarkTextConfig } from '../../services/api';
import { ExclamationTriangleIcon, EyeIcon, PrinterIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AlignmentControl from '../Watermark/AlignmentControl';
import WatermarkPreview from '../Watermark/WatermarkPreview';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';

// Helper function to calculate text from text_config with actual user/company data
// For recipient fields, we use placeholders as they're only available at runtime
function calculateWatermarkText(
  textConfig: WatermarkTextConfig,
  userEmail?: string,
  companyName?: string,
  deviceName?: string
): string {
  // If custom text is enabled, return only the custom text value
  if (textConfig.custom_text && textConfig.custom_text_value) {
    return textConfig.custom_text_value;
  }

  // Build text from enabled options with actual values where available
  const parts: string[] = [];

  if (textConfig.owner_company_name && companyName) {
    parts.push(companyName);
  }
  if (textConfig.recipient_company_name) {
    parts.push('{recipient_company_name}'); // Only available at runtime
  }
  if (textConfig.owner_email && userEmail) {
    parts.push(userEmail);
  }
  if (textConfig.recipient_email) {
    parts.push('{recipient_email}'); // Only available at runtime
  }
  if (textConfig.recipient_ip_address) {
    parts.push('{recipient_ip_address}'); // Only available at runtime
  }
  if (textConfig.timestamp) {
    // Use current timestamp
    const now = new Date();
    const timestamp = now.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    parts.push(timestamp);
  }
  if (textConfig.file_name) {
    parts.push('{file_name}'); // Only available at runtime
  }
  if (textConfig.device_name && deviceName) {
    parts.push(deviceName);
  }

  return parts.join(' | ');
}

interface CompanySecuritySettings {
  screen_watermark_settings?: WatermarkSettings;
  print_watermark_settings?: WatermarkSettings;
  override_screen_watermark_settings?: boolean;
  override_print_watermark_settings?: boolean;
}

const WatermarkTab: React.FC = () => {
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const { setHasUnsavedChanges, setOnSave, setIsSubmitting } = useUnsavedChanges();
  const [companySecuritySettings, setCompanySecuritySettings] = useState<CompanySecuritySettings | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'screen' | 'print'>('screen');
  const [showPreview, setShowPreview] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Screen watermark state
  const [screenWatermark, setScreenWatermark] = useState<WatermarkSettings>({
    text_config: {
      owner_company_name: false,
      recipient_company_name: false,
      owner_email: false,
      recipient_email: false,
      recipient_ip_address: false,
      timestamp: false,
      file_name: false,
      device_name: false,
      custom_text: false,
      custom_text_value: '',
    },
    font: 'Arial',
    font_size: 12,
    color: '#000000',
    opacity: 0.5,
    angle: 45, // Default to diagonal (45°)
  });

  // Print watermark state
  const [printWatermark, setPrintWatermark] = useState<WatermarkSettings>({
    text_config: {
      owner_company_name: false,
      recipient_company_name: false,
      owner_email: false,
      recipient_email: false,
      recipient_ip_address: false,
      timestamp: false,
      file_name: false,
      device_name: false,
      custom_text: false,
      custom_text_value: '',
    },
    font: 'Arial',
    font_size: 12,
    color: '#000000',
    opacity: 0.5,
    angle: 45, // Default to diagonal (45°)
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load user preferences
        const preferencesData = await apiService.getUserPreferences();
        const prefs = preferencesData.preferences || preferencesData;

        // Set initial watermark values from user preferences
        if (prefs.screen_watermark_settings) {
          const settings = prefs.screen_watermark_settings;
          // Handle backward compatibility - if text_config doesn't exist, create default
          if (!settings.text_config) {
            settings.text_config = {
              owner_company_name: false,
              recipient_company_name: false,
              owner_email: false,
              recipient_email: false,
              recipient_ip_address: false,
              timestamp: false,
              file_name: false,
              device_name: false,
              custom_text: false,
              custom_text_value: '',
            };
          }
          // Normalize angle to 45° or -45° (diagonal only)
          if (settings.angle === undefined) {
            // Check if old alignment field exists
            if ((settings as any).alignment !== undefined) {
              const oldAlign = (settings as any).alignment;
              if (typeof oldAlign === 'number') {
                // Convert old alignment to new angle format (45 or -45)
                if (oldAlign === 0) {
                  settings.angle = 45; // Default to diagonal
                } else if (oldAlign === 90) {
                  settings.angle = -45; // Convert vertical to negative diagonal
              } else {
                  settings.angle = oldAlign === -45 ? -45 : 45; // Keep -45, otherwise default to 45
              }
            } else {
                settings.angle = 45; // Default to diagonal
              }
            } else {
              settings.angle = 45; // Default to diagonal
            }
          } else if (typeof settings.angle !== 'number') {
            settings.angle = 45;
          } else {
            // Normalize angle to only 45 or -45
            if (settings.angle !== 45 && settings.angle !== -45) {
              settings.angle = 45; // Default to diagonal if not valid
            }
          }
          setScreenWatermark(settings);
        }
        if (prefs.print_watermark_settings) {
          const settings = prefs.print_watermark_settings;
          // Handle backward compatibility - if text_config doesn't exist, create default
          if (!settings.text_config) {
            settings.text_config = {
              owner_company_name: false,
              recipient_company_name: false,
              owner_email: false,
              recipient_email: false,
              recipient_ip_address: false,
              timestamp: false,
              file_name: false,
              device_name: false,
              custom_text: false,
              custom_text_value: '',
            };
          }
          // Normalize angle to 45° or -45° (diagonal only)
          if (settings.angle === undefined) {
            // Check if old alignment field exists
            if ((settings as any).alignment !== undefined) {
              const oldAlign = (settings as any).alignment;
              if (typeof oldAlign === 'number') {
                // Convert old alignment to new angle format (45 or -45)
                if (oldAlign === 0) {
                  settings.angle = 45; // Default to diagonal
                } else if (oldAlign === 90) {
                  settings.angle = -45; // Convert vertical to negative diagonal
              } else {
                  settings.angle = oldAlign === -45 ? -45 : 45; // Keep -45, otherwise default to 45
              }
            } else {
                settings.angle = 45; // Default to diagonal
              }
            } else {
              settings.angle = 45; // Default to diagonal
            }
          } else if (typeof settings.angle !== 'number') {
            settings.angle = 45;
          } else {
            // Normalize angle to only 45 or -45
            if (settings.angle !== 45 && settings.angle !== -45) {
              settings.angle = 45; // Default to diagonal if not valid
            }
          }
          setPrintWatermark(settings);
        }

        // Load company security settings only to check if override is enabled (for all users)
        // Admin/sysadmin users in settings tab use their own preferences, not company settings
        if (user?.company_id) {
          try {
            const companySettings = await apiService.getCompanySecuritySettings(user.company_id);
            setCompanySecuritySettings(companySettings);
            
            // Also fetch company name for watermark text calculation
            try {
              const companyResponse = await apiService.get(`/companies/${user.company_id}`);
              setCompanyName(companyResponse.data.name || null);
          } catch (err) {
              console.error('Failed to load company name:', err);
          }
          } catch (err) {
            console.error('Failed to load company settings:', err);
          }
        }
      } catch (err: any) {
        console.error('Failed to load watermark settings:', err);
        setError(t('watermark.loadFailed'));
        toast.error(t('watermark.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user, t]);

  // Get effective watermark settings (company override or user preference)
  const getEffectiveScreenWatermark = (): WatermarkSettings => {
    if (companySecuritySettings?.override_screen_watermark_settings && companySecuritySettings.screen_watermark_settings) {
      return companySecuritySettings.screen_watermark_settings;
    }
    return screenWatermark;
  };

  const getEffectivePrintWatermark = (): WatermarkSettings => {
    if (companySecuritySettings?.override_print_watermark_settings && companySecuritySettings.print_watermark_settings) {
      return companySecuritySettings.print_watermark_settings;
    }
    return printWatermark;
  };

  // Check if user can edit watermark settings
  const canEditScreenWatermark = !companySecuritySettings?.override_screen_watermark_settings;
  const canEditPrintWatermark = !companySecuritySettings?.override_print_watermark_settings;


  // Handle other text config checkboxes - if custom_text is enabled, disable it
  const handleTextConfigChange = (type: 'screen' | 'print', field: string, value: boolean) => {
    if (type === 'screen' && canEditScreenWatermark) {
      const newConfig = { ...screenWatermark.text_config, [field]: value };
      // If enabling any other option, disable custom_text and clear custom_text_value
      if (value && field !== 'custom_text') {
        newConfig.custom_text = false;
        newConfig.custom_text_value = '';
      }
      setScreenWatermark({
        ...screenWatermark,
        text_config: newConfig,
      });
      setHasChanges(true);
    } else if (type === 'print' && canEditPrintWatermark) {
      const newConfig = { ...printWatermark.text_config, [field]: value };
      // If enabling any other option, disable custom_text and clear custom_text_value
      if (value && field !== 'custom_text') {
        newConfig.custom_text = false;
        newConfig.custom_text_value = '';
      }
      setPrintWatermark({
        ...printWatermark,
        text_config: newConfig,
      });
      setHasChanges(true);
    }
  };


  // Handle watermark changes from preview
  const handleWatermarkChange = (updatedWatermark: WatermarkSettings) => {
    if (activeTab === 'screen') {
      setScreenWatermark(updatedWatermark);
    } else {
      setPrintWatermark(updatedWatermark);
    }
    setHasChanges(true);
  };

  // Use refs to store the latest watermark state so save handlers don't need them in dependencies
  const screenWatermarkRef = useRef(screenWatermark);
  const printWatermarkRef = useRef(printWatermark);

  useEffect(() => {
    screenWatermarkRef.current = screenWatermark;
  }, [screenWatermark]);

  useEffect(() => {
    printWatermarkRef.current = printWatermark;
  }, [printWatermark]);

  // Track if we're currently saving to prevent auto-saves
  const isSavingRef = useRef(false);

  const handleSaveScreenWatermark = useCallback(async () => {
    if (!user || isSavingRef.current) return;

    try {
      isSavingRef.current = true;
      setSaving(true);
      setIsSubmitting(true);
      setError(null);

      // Prepare text_config - ensure custom_text and combination are mutually exclusive
      const textConfig = { ...screenWatermarkRef.current.text_config };
      
      // If custom_text is true, clear all other options
      if (textConfig.custom_text) {
        textConfig.owner_company_name = false;
        textConfig.recipient_company_name = false;
        textConfig.owner_email = false;
        textConfig.recipient_email = false;
        textConfig.recipient_ip_address = false;
        textConfig.timestamp = false;
        textConfig.file_name = false;
        textConfig.device_name = false;
      } else {
        // If combination mode, clear custom_text_value
        textConfig.custom_text_value = '';
      }

      // Calculate text from text_config with actual user/company data
      const calculatedText = calculateWatermarkText(
        textConfig,
        user.email,
        companyName || undefined,
        user.device_name
      );

      // All users (including admin/sysadmin) use user preferences in settings tab
      const settingsToSave = {
        ...screenWatermarkRef.current,
        text: calculatedText,
        text_config: textConfig,
        angle: screenWatermarkRef.current.angle === -45 ? -45 : 45, // Normalize to 45 or -45
      };
      
        const updatedPreferences: any = {
        screen_watermark_settings: settingsToSave,
        };

        await apiService.updateUserPreferences(updatedPreferences);

        setHasChanges(false);
        toast.success(t('watermark.saved'));
    } catch (err: any) {
      console.error('Failed to save screen watermark settings:', err);
setError(t('watermark.saveScreenFailed'));
        toast.error(err.response?.data?.error || t('watermark.saveScreenFailed'));
    } finally {
      setSaving(false);
      setIsSubmitting(false);
      isSavingRef.current = false;
    }
  }, [user, companyName, setSaving, setIsSubmitting, setError, setHasChanges, t]);

  const handleSavePrintWatermark = useCallback(async () => {
    if (!user || isSavingRef.current) return;

    try {
      isSavingRef.current = true;
      setSaving(true);
      setIsSubmitting(true);
      setError(null);

      // Prepare text_config - ensure custom_text and combination are mutually exclusive
      const textConfig = { ...printWatermarkRef.current.text_config };
      
      // If custom_text is true, clear all other options
      if (textConfig.custom_text) {
        textConfig.owner_company_name = false;
        textConfig.recipient_company_name = false;
        textConfig.owner_email = false;
        textConfig.recipient_email = false;
        textConfig.recipient_ip_address = false;
        textConfig.timestamp = false;
        textConfig.file_name = false;
        textConfig.device_name = false;
      } else {
        // If combination mode, clear custom_text_value
        textConfig.custom_text_value = '';
      }

      // Calculate text from text_config with actual user/company data
      const calculatedText = calculateWatermarkText(
        textConfig,
        user.email,
        companyName || undefined,
        user.device_name
      );

      // All users (including admin/sysadmin) use user preferences in settings tab
      const settingsToSave = {
        ...printWatermarkRef.current,
        text: calculatedText,
        text_config: textConfig,
        angle: printWatermarkRef.current.angle === -45 ? -45 : 45, // Normalize to 45 or -45
      };
      
        const updatedPreferences: any = {
        print_watermark_settings: settingsToSave,
        };

        await apiService.updateUserPreferences(updatedPreferences);

        setHasChanges(false);
        toast.success(t('watermark.printSaved'));
    } catch (err: any) {
      console.error('Failed to save print watermark settings:', err);
setError(t('watermark.savePrintFailed'));
        toast.error(err.response?.data?.error || t('watermark.savePrintFailed'));
    } finally {
      setSaving(false);
      setIsSubmitting(false);
      isSavingRef.current = false;
    }
  }, [user, companyName, setSaving, setIsSubmitting, setError, setHasChanges, t]);

  // Use refs to store the latest save handlers to avoid unnecessary re-renders
  const handleSaveScreenWatermarkRef = useRef(handleSaveScreenWatermark);
  const handleSavePrintWatermarkRef = useRef(handleSavePrintWatermark);

  useEffect(() => {
    handleSaveScreenWatermarkRef.current = handleSaveScreenWatermark;
  }, [handleSaveScreenWatermark]);

  useEffect(() => {
    handleSavePrintWatermarkRef.current = handleSavePrintWatermark;
  }, [handleSavePrintWatermark]);

  // Set up unsaved changes tracking
  // IMPORTANT: This only sets the onSave function, it does NOT call it
  // The save will only happen when the user explicitly clicks the save button in the header
  // Using double arrow function pattern to prevent React from calling it immediately
  useEffect(() => {
    if (hasChanges) {
      setHasUnsavedChanges(true);
      // Use double arrow function pattern like UserDetails.tsx to prevent auto-calling
      setOnSave(() => () => {
        // This function is ONLY called from the header save button onClick handler
        if (activeTab === 'screen') {
          handleSaveScreenWatermarkRef.current();
        } else {
          handleSavePrintWatermarkRef.current();
        }
      });
    } else {
      setHasUnsavedChanges(false);
      setOnSave(null);
    }

    return () => {
      setOnSave(null);
    };
  }, [hasChanges, activeTab, setHasUnsavedChanges, setOnSave]);

  // Listen for preview event from header (only when on settings page with watermark tab active)
  useEffect(() => {
    const handlePreview = () => {
      setShowPreview(true);
    };
    const handleClosePreview = () => {
      setShowPreview(false);
    };
    const handleSave = () => {
      if (activeTab === 'screen') {
        handleSaveScreenWatermark();
      } else {
        handleSavePrintWatermark();
      }
    };
    window.addEventListener('open-watermark-preview', handlePreview);
    window.addEventListener('close-watermark-preview', handleClosePreview);
    window.addEventListener('watermark-preview-save', handleSave);
    return () => {
      window.removeEventListener('open-watermark-preview', handlePreview);
      window.removeEventListener('close-watermark-preview', handleClosePreview);
      window.removeEventListener('watermark-preview-save', handleSave);
    };
  }, [activeTab, handleSaveScreenWatermark, handleSavePrintWatermark]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-governate-500"></div>
      </div>
    );
  }

  const effectiveScreenWatermark = getEffectiveScreenWatermark();
  const effectivePrintWatermark = getEffectivePrintWatermark();
  const currentWatermark = activeTab === 'screen' ? effectiveScreenWatermark : effectivePrintWatermark;
  const currentEditableWatermark = activeTab === 'screen' ? screenWatermark : printWatermark;
  const canEdit = activeTab === 'screen' ? canEditScreenWatermark : canEditPrintWatermark;

  // Render watermark form fields
  const renderWatermarkForm = () => {
    const watermark = canEdit ? currentEditableWatermark : currentWatermark;
    const effective = currentWatermark;

    return (
      <div className="space-y-4">
        {/* Display Items Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('watermark.displayItems')}
          </label>
          <div className="space-y-3 border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-900">
            {/* Radio buttons for text mode */}
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`text-mode-${activeTab}`}
                  checked={!watermark.text_config.custom_text}
                  onChange={() => {
                    if (canEdit) {
                      const updatedTextConfig = { 
                        ...watermark.text_config, 
                        custom_text: false,
                        custom_text_value: '' // Clear custom text value when switching to combination
                      };
                      if (activeTab === 'screen') {
                        setScreenWatermark({ ...screenWatermark, text_config: updatedTextConfig });
                      } else {
                        setPrintWatermark({ ...printWatermark, text_config: updatedTextConfig });
                      }
                      setHasChanges(true);
                    }
                  }}
                  disabled={!canEdit || saving}
                  className="mr-2 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('watermark.combination')}</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`text-mode-${activeTab}`}
                  checked={watermark.text_config.custom_text || false}
                  onChange={() => {
                    if (canEdit) {
                      const updatedTextConfig = {
                        ...watermark.text_config,
                        custom_text: true,
                        owner_company_name: false,
                        recipient_company_name: false,
                        owner_email: false,
                        recipient_email: false,
                        recipient_ip_address: false,
                        timestamp: false,
                        file_name: false,
                        device_name: false,
                      };
                      if (activeTab === 'screen') {
                        setScreenWatermark({ ...screenWatermark, text_config: updatedTextConfig });
                      } else {
                        setPrintWatermark({ ...printWatermark, text_config: updatedTextConfig });
                      }
                      setHasChanges(true);
                    }
                  }}
                  disabled={!canEdit || saving}
                  className="mr-2 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('watermark.customText')}</span>
              </label>
            </div>

            {/* Show checkboxes only when Combination is selected */}
            {!watermark.text_config.custom_text && (
              <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={canEdit ? watermark.text_config.owner_company_name : effective.text_config.owner_company_name}
                    onChange={(e) => canEdit && handleTextConfigChange(activeTab, 'owner_company_name', e.target.checked)}
                    disabled={!canEdit || saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('watermark.ownerCompanyName')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={canEdit ? watermark.text_config.recipient_company_name : effective.text_config.recipient_company_name}
                    onChange={(e) => canEdit && handleTextConfigChange(activeTab, 'recipient_company_name', e.target.checked)}
                    disabled={!canEdit || saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('watermark.recipientCompanyName')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={canEdit ? watermark.text_config.owner_email : effective.text_config.owner_email}
                    onChange={(e) => canEdit && handleTextConfigChange(activeTab, 'owner_email', e.target.checked)}
                    disabled={!canEdit || saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('watermark.ownerEmail')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={canEdit ? watermark.text_config.recipient_email : effective.text_config.recipient_email}
                    onChange={(e) => canEdit && handleTextConfigChange(activeTab, 'recipient_email', e.target.checked)}
                    disabled={!canEdit || saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('watermark.recipientEmail')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={canEdit ? watermark.text_config.recipient_ip_address : effective.text_config.recipient_ip_address}
                    onChange={(e) => canEdit && handleTextConfigChange(activeTab, 'recipient_ip_address', e.target.checked)}
                    disabled={!canEdit || saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('watermark.recipientIpAddress')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={canEdit ? watermark.text_config.timestamp : effective.text_config.timestamp}
                    onChange={(e) => canEdit && handleTextConfigChange(activeTab, 'timestamp', e.target.checked)}
                    disabled={!canEdit || saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('watermark.timestamp')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={canEdit ? watermark.text_config.file_name : effective.text_config.file_name}
                    onChange={(e) => canEdit && handleTextConfigChange(activeTab, 'file_name', e.target.checked)}
                    disabled={!canEdit || saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('watermark.fileName')}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={canEdit ? watermark.text_config.device_name : effective.text_config.device_name}
                    onChange={(e) => canEdit && handleTextConfigChange(activeTab, 'device_name', e.target.checked)}
                    disabled={!canEdit || saving}
                    className="mr-2 rounded border-gray-300 text-governate-600 focus:ring-governate-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('watermark.deviceName')}</span>
                </label>
              </div>
            )}

            {/* Show custom text input only when Custom Text is selected */}
            {watermark.text_config.custom_text && (
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <input
                  type="text"
                  value={canEdit ? watermark.text_config.custom_text_value : effective.text_config.custom_text_value}
                  onChange={(e) => {
                    if (canEdit) {
                      if (activeTab === 'screen') {
                        setScreenWatermark({
                          ...screenWatermark,
                          text_config: { ...screenWatermark.text_config, custom_text_value: e.target.value }
                        });
                      } else {
                        setPrintWatermark({
                          ...printWatermark,
                          text_config: { ...printWatermark.text_config, custom_text_value: e.target.value }
                        });
                      }
                      setHasChanges(true);
                    }
                  }}
                  disabled={!canEdit || saving}
                  placeholder={t('watermark.customTextPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            )}
          </div>
        </div>

        {/* First row: Font, Font Size */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('watermark.font')}
            </label>
            <select
              value={canEdit ? watermark.font : effective.font}
              onChange={(e) => {
                if (canEdit) {
                  if (activeTab === 'screen') {
                    setScreenWatermark({ ...screenWatermark, font: e.target.value });
                  } else {
                    setPrintWatermark({ ...printWatermark, font: e.target.value });
                  }
                  setHasChanges(true);
                }
              }}
              disabled={!canEdit || saving}
              className="inline-block min-w-[140px] max-w-[200px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="Arial">{t('watermark.fontArial')}</option>
              <option value="Times New Roman">{t('watermark.fontTimesNewRoman')}</option>
              <option value="Courier New">{t('watermark.fontCourierNew')}</option>
              <option value="Helvetica">{t('watermark.fontHelvetica')}</option>
              <option value="Verdana">{t('watermark.fontVerdana')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('watermark.fontSize')}
            </label>
            <input
              type="number"
              value={canEdit ? watermark.font_size : effective.font_size}
              onChange={(e) => {
                if (canEdit) {
                  if (activeTab === 'screen') {
                    setScreenWatermark({ ...screenWatermark, font_size: parseInt(e.target.value) || 12 });
                  } else {
                    setPrintWatermark({ ...printWatermark, font_size: parseInt(e.target.value) || 12 });
                  }
                  setHasChanges(true);
                }
              }}
              disabled={!canEdit || saving}
              min="8"
              max="72"
              className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Second row: Color, Opacity, Rotation Angle */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('watermark.color')}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={canEdit ? watermark.color : effective.color}
                onChange={(e) => {
                  if (canEdit) {
                    if (activeTab === 'screen') {
                      setScreenWatermark({ ...screenWatermark, color: e.target.value });
                    } else {
                      setPrintWatermark({ ...printWatermark, color: e.target.value });
                    }
                    setHasChanges(true);
                  }
                }}
                disabled={!canEdit || saving}
                className="h-10 w-20 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <input
                type="text"
                value={canEdit ? watermark.color : effective.color}
                onChange={(e) => {
                  if (canEdit) {
                    if (activeTab === 'screen') {
                      setScreenWatermark({ ...screenWatermark, color: e.target.value });
                    } else {
                      setPrintWatermark({ ...printWatermark, color: e.target.value });
                    }
                    setHasChanges(true);
                  }
                }}
                disabled={!canEdit || saving}
                placeholder="#000000"
                className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-governate-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('watermark.opacityLabel', { value: canEdit ? watermark.opacity : effective.opacity })}
            </label>
            <input
              type="range"
              value={canEdit ? watermark.opacity : effective.opacity}
              onChange={(e) => {
                if (canEdit) {
                  if (activeTab === 'screen') {
                    setScreenWatermark({ ...screenWatermark, opacity: parseFloat(e.target.value) });
                  } else {
                    setPrintWatermark({ ...printWatermark, opacity: parseFloat(e.target.value) });
                  }
                  setHasChanges(true);
                }
              }}
              disabled={!canEdit || saving}
              min="0"
              max="1"
              step="0.1"
              className="w-full max-w-[200px] h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('watermark.rotationAngle')}
            </label>
            <AlignmentControl
              value={(() => {
                const angleValue = canEdit ? watermark.angle : effective.angle;
                if (typeof angleValue === 'number') {
                  // Normalize to only 45 or -45
                  if (angleValue === -45) return -45;
                  return 45; // Default to diagonal
                }
                return 45; // Default to diagonal
              })()}
              onChange={(degrees) => {
                if (canEdit) {
                  // Ensure only 45 or -45 are set
                  const normalizedAngle = degrees === -45 ? -45 : 45;
                  if (activeTab === 'screen') {
                    setScreenWatermark({ ...screenWatermark, angle: normalizedAngle });
                  } else {
                    setPrintWatermark({ ...printWatermark, angle: normalizedAngle });
                  }
                  setHasChanges(true);
                }
              }}
              disabled={!canEdit || saving}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-black shadow overflow-hidden sm:rounded-lg border border-gray-200 dark:border-gray-900 transition-colors">
        <div className="border-b border-gray-200 dark:border-gray-800">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('screen')}
              className={`py-4 px-6 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === 'screen'
                  ? 'border-governate-500 text-governate-600 dark:text-governate-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <EyeIcon className="w-5 h-5" />
              <span>{t('watermark.screenWatermark')}</span>
            </button>
            <button
              onClick={() => setActiveTab('print')}
              className={`py-4 px-6 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === 'print'
                  ? 'border-governate-500 text-governate-600 dark:text-governate-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <PrinterIcon className="w-5 h-5" />
              <span>{t('watermark.printWatermark')}</span>
            </button>
          </nav>
        </div>

        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                {activeTab === 'screen' ? t('watermark.screenWatermarkSettings') : t('watermark.printWatermarkSettings')}
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {activeTab === 'screen' ? t('watermark.configureForScreen') : t('watermark.configureForPrint')}
              </p>
            </div>
          </div>

          {((activeTab === 'screen' && !canEditScreenWatermark) || (activeTab === 'print' && !canEditPrintWatermark)) && (
            <div className="mb-4 rounded-lg p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" />
                {t('watermark.managedByCompany', { type: activeTab === 'screen' ? t('watermark.screen') : t('watermark.print') })}
              </p>
            </div>
          )}

          <div className="border-t border-gray-200 dark:border-gray-800 pt-5">
            {renderWatermarkForm()}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <WatermarkPreview
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        watermark={activeTab === 'screen' ? screenWatermark : printWatermark}
        type={activeTab}
        onWatermarkChange={handleWatermarkChange}
      />
    </div>
  );
};

export default WatermarkTab;
